import React from "react"
import {createPortal} from "react-dom"

import {LexicalEditor, NodeKey} from "lexical"
import Popover from "../../../gd-popover/popover-v2"
import PopoverPosition from "../../../gd-popover/popover-position"
import {PopoverAttachmentType} from "../../../gd-popover/@const"
import PopoverMenuOption from "../../../popover-menu/popover-menu/menu-option"
import {
    tableClearCell,
    tableDeleteColumn,
    tableDeleteRow,
    tableDeleteTable,
    tableInsertColumnLeft,
    tableInsertColumnRight,
    tableInsertRowAbove,
    tableInsertRowBelow,
    tableToggleColumnHeader,
    tableToggleRowHeader,
    tableUnmergeCell,
} from "./actions"
import {tableRemoveSorting, tableSortByColumn, TableSortDirection} from "./sorting"

type Props = {
    editor          : LexicalEditor
    cellKey         : NodeKey
    isRowHeader     : boolean
    isColumnHeader  : boolean
    isMerged        : boolean
    canDeleteRow    : boolean
    canDeleteColumn : boolean
    canSort         : boolean
    sortDirection   : null | TableSortDirection
    getTargetFunc   : callback
    onClose         : () => void
}

export default function TableActionMenu(props: Props) {

    const run = (action: (editor: LexicalEditor, cellKey: NodeKey) => void) => {
        return () => {
            action(props.editor, props.cellKey)
            props.onClose()
        }
    }

    const sort = (direction: TableSortDirection) => {
        return () => {
            tableSortByColumn(props.editor, props.cellKey, direction)
            props.onClose()
        }
    }

    const popoverPosition = new PopoverPosition({
        attachment      : "bottom left",
        targetAttachment: "top left",
        attachmentType  : PopoverAttachmentType.HORIZONTAL,
    })

    return (
        createPortal(
            <Popover position       = {popoverPosition}
                     getTargetFunc      = {props.getTargetFunc}>
                <div className  = "ui5-popover-menu icon-fs-16"
                     style      = {{minWidth: 180}}
                >
                    <PopoverMenuOption
                        option  = {{label: props.isRowHeader ? 'Remove row header' : 'Make row header'}}
                        onClick = {run(tableToggleRowHeader)}
                    />
                    <PopoverMenuOption
                        option  = {{label: props.isColumnHeader ? 'Remove column header' : 'Make column header'}}
                        onClick = {run(tableToggleColumnHeader)}
                    />

                    <PopoverMenuOption
                        option  = {{label: 'Clear cell'}}
                        onClick = {run(tableClearCell)}
                    />

                    {props.isMerged &&
                        <PopoverMenuOption
                            option  = {{label: 'Unmerge cell'}}
                            onClick = {run(tableUnmergeCell)}
                        />
                    }

                    {props.canSort &&
                        <>
                            <div className="separator"/>

                            {props.sortDirection &&
                                <PopoverMenuOption
                                    option  = {{label: 'Remove sorting'}}
                                    onClick = {run(tableRemoveSorting)}
                                />
                            }
                            {props.sortDirection !== 'ascending' &&
                                <PopoverMenuOption
                                    option  = {{label: 'Sort ascending'}}
                                    onClick = {sort('ascending')}
                                />
                            }
                            {props.sortDirection !== 'descending' &&
                                <PopoverMenuOption
                                    option  = {{label: 'Sort descending'}}
                                    onClick = {sort('descending')}
                                />
                            }
                        </>}

                    <div className="separator"/>

                    <PopoverMenuOption
                        option  = {{label: 'Insert row above'}}
                        onClick = {run(tableInsertRowAbove)}
                    />
                    <PopoverMenuOption
                        option  = {{label: 'Insert row below'}}
                        onClick = {run(tableInsertRowBelow)}
                    />

                    <div className="separator"/>

                    <PopoverMenuOption
                        option  = {{label: 'Insert column left'}}
                        onClick = {run(tableInsertColumnLeft)}
                    />
                    <PopoverMenuOption
                        option  = {{label: 'Insert column right'}}
                        onClick = {run(tableInsertColumnRight)}
                    />

                    <div className="separator"/>

                    {props.canDeleteColumn &&
                        <PopoverMenuOption
                            option  = {{label: 'Delete column'}}
                            onClick = {run(tableDeleteColumn)}
                        />
                    }
                    {props.canDeleteRow &&
                        <PopoverMenuOption
                            option  = {{label: 'Delete row'}}
                            onClick = {run(tableDeleteRow)}
                        />
                    }

                    <PopoverMenuOption
                        option  = {{label: 'Delete table'}}
                        onClick = {run(tableDeleteTable)}
                    />
                </div>
            </Popover>,
            document.body,
        )
    )
}
